<template>
  <div v-if="all_platforms && route.name != 'event-page'">
    <PageHeaderEnv />
    <div :lang="locale" class="min-h-screen flex flex-col" :class="[pageBackground]">
      <!-- Scrolling down slides the top bar out, leaving only the main header; the slightest scroll
           back up brings it back. It sticks above the viewport rather than shrinking, so the content
           underneath never jumps. Not a transform: that would trap the `fixed` layers inside the
           header (the search backdrop, the mobile search screen) within the header's own box.
           Desktop only: on mobile the header scrolls away with the page. -->
      <div
        id="header"
        ref="header"
        class="relative lg:sticky w-full z-10 transition-[top] duration-200 ease-out"
        :style="lg ? { top: topBarHidden ? `-${topBarHeight}px` : '0px' } : undefined">
        <div ref="topBar">
          <HeaderAlert v-if="!isB2C" strapi-type="buyer-portal" />
          <HeaderTopBar />
        </div>
        <div class=" ">
          <slot name="header">
            <PageHeader />
          </slot>
        </div>
      </div>

      <div id="content" class="flex flex-col h-full justify-between content">
        <PageContent>
          <BreadCrumbs v-if="safePath && route && !excludedPaths.includes(route.path)" :path="safePath" />
          <slot></slot>
        </PageContent>
        <slot name="extra-content"></slot>
        <slot name="footer">
          <PageFooterBranded />
        </slot>
      </div>
    </div>
  </div>

  <template v-else-if="all_platforms">
    <HeaderTopBar v-if="!isB2C && route.name != 'event-page'" />
    <slot></slot>
  </template>
  <div
    v-else
    class="flex flex-col bg-background bg-white w-full h-full absolute flex justify-center items-center left-0 top-0"></div>
</template>

<script setup lang="ts">
import HeaderTopBar from "@/components/page/HeaderTopBar.vue";
import PageHeaderEnv from "./PageHeaderEnv.vue";
import BreadCrumbs from "./BreadCrumbs.vue";
import PageHeader from "./PageHeader.vue";
import PageContent from "./PageContent.vue";
import PageFooterBranded from "./PageFooterBranded.vue";
import { computed, ref, useTemplateRef, watch } from "vue";
import brandconfig from "@/brandconfig";
import { useI18n } from "vue-i18n";
import { setTitle } from "@m-media/vue3-meta-tags";
import { useCssVar, useElementSize, useWindowScroll } from "@vueuse/core";
import { HeaderAlert } from "@xs2event/js-lib/components";
import { useRoute } from "vue-router";
import { useBreakpoints } from "@xs2event/js-lib/util";

const isB2C = brandconfig.BRAND !== "xs2event";

const excludedPaths = ["/", "/partners", "/user/profile "];
const { t, locale } = useI18n();

const route = useRoute();
const pageBackground = computed(() => (route.meta.pageBackground as string) ?? "bg-background");

const props = defineProps<{
  path?: Array<{ label: string; to?: string | Record<string, unknown> }>;
  title?: string;
}>();

const safePath = computed(() => props.path);

const pageTitle = computed(() => {
  return props.title ? ` ${t(props.title)} | ${brandconfig.BRAND_SHORT_TITLE}` : brandconfig.BRAND_TITLE;
});

watch(
  pageTitle,
  (title) => {
    setTitle(title);
  },
  { immediate: true }
);

const all_platforms = ref(true);
const deviceType = ref("");
const userAgent = ref("");
const platform = navigator.userAgent.toLowerCase();

const icon = brandconfig.BRAND_ICON;
if (icon) {
  const iconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (iconLink) iconLink.href = icon;
}
if (/(samsung|android|webos|iphone|blackberry|linux armv81|windows phone)/.test(platform)) {
  deviceType.value = "mobile";
} else if (/mac|win|linux/i.test(platform)) {
  deviceType.value = "desktop";
} else if (/tablet|ipad/i.test(platform)) {
  deviceType.value = "tablet";
} else {
  deviceType.value = "unknown";
}
userAgent.value = navigator.userAgent;
if (brandconfig.MOBILE_ONLY === true) {
  if (deviceType.value !== "mobile" && deviceType.value !== "tablet" && deviceType.value !== "unknown") {
    all_platforms.value = false;
  }
}

const { lg } = useBreakpoints();

const header = useTemplateRef("header");

const { height } = useElementSize(header);
const { height: topBarHeight } = useElementSize(useTemplateRef("topBar"));

// Ignores sub-pixel and trackpad jitter, so the top bar doesn't flicker; anything more is a real scroll up.
const SCROLL_UP_TOLERANCE = 8;

const topBarHidden = ref(false);
const { y: scrollY } = useWindowScroll();
// The lowest point reached while scrolling down. Measuring from here rather than per scroll event
// catches slow scrolls too, which arrive as many 1-2px steps.
let scrollTurnPoint = 0;

watch(scrollY, (current, previous) => {
  // Keep it while its own slot at the top of the page is still in view (also covers overscroll).
  if (current <= topBarHeight.value) {
    topBarHidden.value = false;
  } else if (current > previous) {
    topBarHidden.value = true;
    scrollTurnPoint = current;
  } else if (scrollTurnPoint - current > SCROLL_UP_TOLERANCE) {
    topBarHidden.value = false;
  }
});

const headerHeight = useCssVar("--header-height", document.documentElement);

// Other sticky/fixed elements offset by this, so it tracks what is actually on screen. Nothing on
// mobile, where the header doesn't stick.
watch(
  [height, topBarHeight, topBarHidden, lg],
  ([height, topBarHeight, hidden, lg]) => {
    headerHeight.value = !lg ? "0px" : `${hidden ? height - topBarHeight : height}px`;
  },
  { immediate: true }
);
</script>
<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 250ms ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
